/**
 * Craft Export Transformation Module
 *
 * This module provides pure functions for transforming Craft export markdown
 * content into NotePlan-compatible format. All transformations are applied
 * during the import batch building phase, before link parsing.
 *
 * @module craftTransforms
 */

export type CraftTransformContext = {
	path: string
	isCalendar: boolean
}

/**
 * Detects if a file list contains a Craft export by checking for .assets folders
 *
 * @param files - FileList or File array to check
 * @returns true if Craft export detected, false otherwise
 */
export function detectCraftExport(files: FileList | File[]): boolean {
	// Matches any path segment that ends with ".assets"
	// Handles both / and \ as separators
	const CRAFT_ASSETS_SEGMENT = /(^|[\\/])[^\\/]+\.assets([\\/]|$)/

	const hasAssetsSegment = (p?: string) =>
		typeof p === 'string' && CRAFT_ASSETS_SEGMENT.test(p)

	return [...files].some((file) => {
		if (hasAssetsSegment(file.webkitRelativePath)) {
			return true
		}
		const fileWithPath = file as { path?: string }
		return hasAssetsSegment(fileWithPath.path)
	})
}

/**
 * Applies all Craft-specific transformations to markdown content
 *
 * Transformations are applied in a specific order:
 * 1. Remove duplicate date heading (calendar notes only)
 * 2. Transform Craft date links (day://YYYY.MM.DD)
 * 3. Transform Craft wiki links (remove folder paths)
 * 4. Shorten Craft block IDs
 * 5. Ensure image links have ! prefix
 * 6. Replace 3-space indentation with tabs
 *
 * @param content - Original markdown content
 * @param ctx - Transform context with path and calendar info
 * @returns Transformed markdown content
 */
export function applyCraftTransforms(
	content: string,
	context: CraftTransformContext
): string {
	let transformed = content

	// Apply transformations in order
	if (context.isCalendar) {
		transformed = removeDuplicateDateHeading(transformed)
	}

	transformed = transformCraftDateLinks(transformed)
	transformed = transformCraftWikiLinks(transformed)
	transformed = shortenCraftBlockIds(transformed)
	transformed = ensureImageBang(transformed)
	transformed = replaceThreeSpacesWithTab(transformed)

	return transformed
}

/**
 * Removes duplicate date heading from daily notes
 *
 * Craft daily notes often have a heading like "# 2025.09.02" as the first line,
 * which duplicates the calendar note title. This function removes it.
 *
 * @param content - Markdown content
 * @returns Content with first line removed if it's a date heading
 */
function removeDuplicateDateHeading(content: string): string {
	const lines = content.split('\n')
	if (lines.length > 0 && /^#\s+\d{4}(?:[./-]\d{2}){2}/.test(lines[0])) {
		return lines.slice(1).join('\n')
	}
	return content
}

/**
 * Transforms Craft date links to NotePlan wiki link format
 *
 * Converts: [text](day://YYYY.MM.DD) → [[YYYY-MM-DD]]
 * The link text is discarded as NotePlan uses the date as the link text.
 *
 * @param content - Markdown content
 * @returns Content with date links transformed
 */
function transformCraftDateLinks(content: string): string {
	// Pattern: [text](day://YYYY.MM.DD) → [[YYYY-MM-DD]]
	// Negative lookbehind ensures we don't match image links or inline code
	const CRAFT_DATE_LINK =
		/(?<![!`])\[([^\]]*)]\(day:\/\/(\d{4})\.(\d{2})\.(\d{2})\)/g

	return content.replaceAll(
		CRAFT_DATE_LINK,
		(_match, _text, year: string, month: string, day: string) => {
			return `[[${year}-${month}-${day}]]`
		}
	)
}

/**
 * Transforms Craft wiki links by removing folder paths
 *
 * Converts: [[folder/file]] → [[file]]
 * Preserves: [[file]] → [[file]] (no change)
 *
 * Note: Does not transform image wiki links (those starting with !)
 *
 * @param content - Markdown content
 * @returns Content with wiki link paths stripped
 */
function transformCraftWikiLinks(content: string): string {
	// Pattern: [[folder/file]] → [[file]]
	// Negative lookbehind ensures we don't match image wiki links
	const CRAFT_WIKI_LINK = /(?<!!)\[\[([^\]]*)]]/g

	return content.replaceAll(CRAFT_WIKI_LINK, (match, linkText: string) => {
		// Remove path, keep only basename
		const lastSlash = linkText.lastIndexOf('/')
		if (lastSlash === -1) return match

		const basename = linkText.slice(lastSlash + 1)
		return `[[${basename}]]`
	})
}

/**
 * Shortens Craft block IDs to 6 characters
 *
 * Converts: ^9D8F2C7A-B3E7-4FC0-93C8-... → ^9d8f2c
 * Takes the first 6 characters after the ^ and lowercases them.
 *
 * Note: This may cause collisions in documents with many block references,
 * but improves readability significantly.
 *
 * @param content - Markdown content
 * @returns Content with shortened block IDs
 */
function shortenCraftBlockIds(content: string): string {
	// Pattern: ^LONG-BLOCK-ID → ^shortid
	// Matches block IDs that are 32+ characters (UUID format with or without hyphens)
	const CRAFT_BLOCK_ID = /\^[\da-z-]{32,}/gi

	return content.replaceAll(CRAFT_BLOCK_ID, (match) => {
		// Take first 6 non-hyphen chars (skip ^), lowercase
		const chars = match.slice(1).replaceAll('-', '')
		const shortId = chars.slice(0, 6).toLowerCase()
		return `^${shortId}`
	})
}

/**
 * Ensures image file links have the ! prefix
 *
 * Converts: [alt](image.png) → ![alt](image.png)
 * Preserves: ![alt](image.png) → ![alt](image.png) (no change)
 *
 * Supports common image extensions: png, jpg, jpeg, gif, webp, svg, bmp
 *
 * @param content - Markdown content
 * @returns Content with image links properly prefixed
 */
function ensureImageBang(content: string): string {
	// Find markdown links to image files that don't have ! prefix
	// Negative lookbehind ensures we don't add ! to already-correct image links
	// Match image extensions case-insensitively
	// Use lazy matching to handle URLs with parentheses and other special characters
	return content.replaceAll(
		/(?<!!)\[([^\]]*)]\((.+?\.(?:png|jpg|jpeg|gif|webp|svg|bmp)(?:[^)]*)?)\)/gi,
		(_match, text: string, url: string) => `![${text}](${url})`
	)
}

/**
 * Replaces 3-space indentation with tabs
 *
 * Craft uses 3 spaces for indentation, while NotePlan uses tabs.
 * This function converts leading 3-space groups to tabs.
 *
 * Examples:
 * - "   text" → "\ttext"
 * - "      text" → "\t\ttext"
 * - "text   text" → "text   text" (no change, not at line start)
 *
 * @param content - Markdown content
 * @returns Content with 3-space indentation replaced by tabs
 */
function replaceThreeSpacesWithTab(content: string): string {
	// Replace 3-space indentation with tab at line start
	// The + quantifier handles multiple levels of indentation
	return content.replaceAll(/^( {3})+/gm, (match) => {
		const tabCount = match.length / 3
		return '\t'.repeat(tabCount)
	})
}

